const fontSize = {
  name: "font-size",
  path: "/font-size",
  title: "Font Size",
  description: "Utilities for controlling the font size of an element.",
  value: {
    "font-size": {
      "text-xs": "0.75rem /* 12px */",
      "text-sm": "0.875rem /* 14px */",
      "text-base": "1rem /* 16px */",
      "text-lg": "1.125rem /* 18px */",
      "text-xl": "1.25rem /* 20px */",
      "text-2xl": "1.5rem /* 24px */",
      "text-3xl": "1.875rem /* 30px */",
      "text-4xl": "2.25rem /* 36px */",
      "text-5xl": "3rem /* 48px */",
      "text-6xl": "3.75rem /* 60px */",
      "text-7xl": "4.5rem /* 72px */",
      "text-8xl": "6rem /* 96px */",
      "text-9xl": "8rem /* 128px */",
      "text-[<value>]": "<value>",
    }
  },
  exampleData: [
    {
      example: {
        code: `
<p class="text-xs">The quick brown fox jumps over the lazy dog. (text-xs)</p>
<p class="text-sm">The quick brown fox jumps over the lazy dog. (text-sm)</p>
<p class="text-base">The quick brown fox jumps over the lazy dog. (text-base)</p>
<p class="text-lg">The quick brown fox jumps over the lazy dog. (text-lg)</p>
<p class="text-xl">The quick brown fox jumps over the lazy dog. (text-xl)</p>
<p class="text-2xl">The quick brown fox jumps over the lazy dog. (text-2xl)</p>
<p class="text-3xl">The quick brown fox jumps over the lazy dog. (text-3xl)</p>
<p class="text-4xl">The quick brown fox jumps over the lazy dog. (text-4xl)</p>
<p class="text-5xl">The quick brown fox jumps over the lazy dog. (text-5xl)</p>
<p class="text-6xl">The quick brown fox jumps over the lazy dog. (text-6xl)</p>
<p class="text-7xl">The quick brown fox jumps over the lazy dog. (text-7xl)</p>
<p class="text-8xl">The quick brown fox jumps over the lazy dog. (text-8xl)</p>
<p class="text-9xl">The quick brown fox jumps over the lazy dog. (text-9xl)</p>
<p class="text-[2rem]">The quick brown fox jumps over the lazy dog. (text-[2rem])</p>
        `
      }
    },
    {
      example: {
        code: `
<p class="text-sm/6 ...">So I started to walk into the water...</p>
<p class="text-sm/7 ...">So I started to walk into the water...</p>
<p class="text-sm/8 ...">So I started to walk into the water...</p>
        `
      }
    },
  ],
  htmlData: [
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">text-xs</code> through <code class="text-color-gray-800 dark:text-color-white font-semibold">text-3xl</code> to change the font size:
      </p>
      <div class="space-y-6">
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-xs</small>
                <p class="text-xs rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-sm</small>
                <p class="text-sm rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-base</small>
                <p class="text-base rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-lg</small>
                <p class="text-lg rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-xl</small>
                <p class="text-xl rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-2xl</small>
                <p class="text-2xl rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-3xl</small>
                <p class="text-3xl/6 rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
            <div class="flex flex-col gap-2">
                <small class="text-color-gray-500 font-mono">text-[2rem]</small>
                <p class="text-[2rem] rounded">The quick brown fox jumps over the lazy dog.</p>
            </div>
        </div>
      `
    },
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">text-sm/6, text-lg/7</code>
        to set the font size and line-height of an element at the same time:
      </p>
      <div class="flex flex-col gap-8">
            <div><small class="mb-3 font-mono font-medium text-color-gray-500 dark:text-color-gray-400">text-sm/6</small>
                <p class="text-sm/6 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
            <div><small class="mb-3 font-mono font-medium  text-color-gray-500 dark:text-color-gray-400">text-sm/7</small>
                <p class="text-sm/7 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
            <div><small class="mb-3 font-mono font-medium  text-color-gray-500 dark:text-color-gray-400">text-sm/8</small>
                <p class="text-sm/8 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
        </div>
      `
    }
  ]
}
export default fontSize;

<div className="sr-only space-y-6">
    <p class="text-xs bg-color-blue-100 p-2 rounded">The quick brown fox jumps over the lazy dog. (text-xs)</p>
        <p class="text-sm p-2 rounded">The quick brown fox jumps over the lazy dog. (text-sm)</p>
        <p class="text-base p-2 rounded">The quick brown fox jumps over the lazy dog. (text-base)</p>
        <p class="text-lg p-2 rounded">The quick brown fox jumps over the lazy dog. (text-lg)</p>
        <p class="text-xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-xl)</p>
        <p class="text-2xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-2xl)</p>
        <p class="text-3xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-3xl)</p>
        <p class="text-4xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-4xl)</p>
        <p class="text-5xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-5xl)</p>
        <p class="text-6xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-6xl)</p>
        <p class="text-7xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-7xl)</p>
        <p class="text-8xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-8xl)</p>
        <p class="text-9xl p-2 rounded">The quick brown fox jumps over the lazy dog. (text-9xl)</p>
        <p class="text-[2rem] p-2 rounded">The quick brown fox jumps over the lazy dog. (text-[2rem])</p>

              <div class="flex flex-col gap-8">
            <div><span class="mb-3 font-mono text-xs font-semibold text-color-gray-500 dark:text-color-gray-400">text-sm/6</span>
                <p class="text-sm/6 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
            <div><span class="mb-3 font-mono text-xs font-semibold text-gray-500 dark:text-gray-400">text-sm/7</span>
                <p class="text-sm/7 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
            <div><span class="mb-3 font-mono text-xs font-semibold text-gray-500 dark:text-gray-400">text-sm/8</span>
                <p class="text-sm/8 text-color-gray-900 dark:text-color-gray-200">So I started to walk into the water. I won't lie to you boys, I was terrified. But I pressed on, and as I made my way past the breakers a strange calm came over me. I don't know if it was divine intervention or the kinship of all living things
                    but I tell you Jerry at that moment, I <em>was</em> a marine biologist.</p>
            </div>
        </div>
</div>